import { ProductOut, ProductPriceOut, StockDetails } from '../../../store/api';
import { SubscriptionRenewalLineUpdateRequest } from '../../../api/subscription-management/renewals/updateRenewalLine';
import { IssueUsage } from '../../../shared/subscription-management/types/IssueUsage';
import {
    ExtendedManageLineShared,
    ExtendedManageLineUsageLimit,
    ExtendedManageLineUsageLimitNone,
    ExtendedManageLineUsageLimitValue
} from '../../../shared/subscription-management/types/ExtendedManageLineShared';

export enum RenewalType {
    RECOMMENDED = 'RECOMMENDED',
    SAME_AS_CURRENT_YEAR = 'SAME_AS_CURRENT_YEAR',
    CUSTOM = 'CUSTOM',
    DONT_RENEW = 'DONT_RENEW',
    ALREADY_RENEWED = 'ALREADY_RENEWED',
    UNAVAILABLE = 'UNAVAILABLE'
}

export enum ManageRenewalCells {
    Simple = 'Simple',
    Extended = 'Extended'
}

export type SimpleManageRenewalCells = {
    type: ManageRenewalCells.Simple;
    list: SimpleRenewalTile[];
};
export type ExtendedManageRenewalCells = {
    type: ManageRenewalCells.Extended;
    list: ExtendedRenewalTile[];
};

/**
 * SIMPLE
 */

export type SimpleRenewalTile = {
    product: ProductOut;
    userSubscribedLabel?: string;
    showWarning?: 'orange' | 'red';
    currentYearSubscriptions: SimpleRenewalUsageLimitValue | SimpleRenewalUsageLimitNone;
    nextYearRenewalLine: SimpleRenewalLine;
    previousIssueUsage?: IssueUsage;
    currentIssueUsage?: IssueUsage;
};

export enum SimpleRenewalUsageLimit {
    Value = 'Value',
    None = 'None'
}

export type SimpleRenewalUsageLimitValue = {
    type: SimpleRenewalUsageLimit.Value;
    label: string;
    stockDetails: StockDetails;
};
export type SimpleRenewalUsageLimitNone = {
    type: SimpleRenewalUsageLimit.None;
    label: string;
};
export type SimpleRenewalLine = RenewalLine | AlreadyRenewedLine | UnavailableLine;

/**
 * EXTENDED
 */

export type ExtendedRenewalTile = {
    product: ProductOut;
    overview: ExtendedRenewalOverview;
    individual: ExtendedRenewalIndividual;
    shared: ExtendedManageLineShared;
};

export type ExtendedRenewalOverview = {
    product: ProductOut;
    userSubscribedLabel?: string;
    showWarning?: 'orange' | 'red';
    currentYearSubscription: ExtendedRenewalOverviewUsageLimitValue | ExtendedRenewalOverviewUsageLimitNone;
    nextYearSubscription: ExtendedRenewalOverviewUsageLimitValue | ExtendedRenewalOverviewUsageLimitNone;
    previousIssueUsage?: IssueUsage;
    currentIssueUsage?: IssueUsage;
};
export type ExtendedRenewalOverviewUsageLimitValue = {
    type: ExtendedManageLineUsageLimit.Value;
    overviewLabel: string;
    individuellLabel: string;
    sharedLabel: string;
    stockDetails: StockDetails;
};
export type ExtendedRenewalOverviewUsageLimitNone = {
    type: ExtendedManageLineUsageLimit.None;
    label: string;
};

export type ExtendedRenewalIndividual = {
    currentYearSubscription: ExtendedManageLineUsageLimitValue | ExtendedManageLineUsageLimitNone;
    nextYearRenewalLine: ExtendedRenewalIndividualNextYearRenewalLine;
    previousIssueUsage?: IssueUsage;
    currentIssueUsage?: IssueUsage;
};
export type ExtendedRenewalIndividualNextYearRenewalLine =
    | RenewalLine
    | AlreadyRenewedLine
    | UnavailableLine
    | ExtendedAdaptedRecommendedLine
    | ExtendedAdaptedCustomLine;
export type ExtendedAdaptedRecommendedLine = {
    type: RenewalLineView.Adapted_Recommended;
    selectedType: RenewalType;
    types: RenewalTypes[];
    items: RenewalLineItem[];
    total?: RenewalTotal;
    onRenewalLineUpdate: (params: SubscriptionRenewalLineUpdateRequest) => void;
};
export type ExtendedAdaptedCustomLine = {
    type: RenewalLineView.Adapted_Custom;
    selectedType: RenewalType;
    types: RenewalTypes[];
    items: RenewalLineItem[];
    total?: RenewalTotal;
    onRenewalLineUpdate: (params: SubscriptionRenewalLineUpdateRequest) => void;
};

/**
 * Renewal Line
 */

export enum RenewalLineView {
    RenewalLine = 'RenewalLine',
    Unavailable = 'Unavailable',
    Renewed = 'Renewed',
    Adapted_Recommended = 'Adapted_Recommended',
    Adapted_Custom = 'Adapted_Custom'
}

export type RenewalLine = {
    type: RenewalLineView.RenewalLine;
    selectedType: RenewalType;
    types: RenewalTypes[];
    items: RenewalLineItem[];
    total?: RenewalTotal;
    onRenewalLineUpdate: (params: SubscriptionRenewalLineUpdateRequest) => void;
};
export type RenewalTypes = {
    type: RenewalType;
    label: string;
    selected: boolean;
    usageLimit?: string;
    enabled: boolean;
    infoMessage?: string;
};
export type RenewalLineItem = {
    amount: number;
    price: ProductPriceOut;
};
export type RenewalTotal = {
    usageLimitLabel: string;
    priceLabel: string;
};

export type UnavailableLine = {
    type: RenewalLineView.Unavailable;
    selectedType: RenewalType.UNAVAILABLE;
};
export type AlreadyRenewedLine = {
    type: RenewalLineView.Renewed;
    selectedType: RenewalType.ALREADY_RENEWED;
    label: string;
};
